import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { RewardStatusBadge } from "@/components/rewards/RewardStatusBadge";
import { type RewardData } from "@/components/referrals/tableConfigs";
import { RewardStatus } from "@/lib/schema";
import { formatRewardAmount, formatDateForSouthAfrica } from "@/lib/textUtils";

interface RewardTableItemProps {
  reward: RewardData;
  index: number;
  onRedeemClick?: (reward: RewardData) => void;
}

export function RewardTableItem({ reward, index, onRedeemClick }: RewardTableItemProps) {
  const getInitials = (name: string) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase();
  };

  const renderActionButton = (isMobile: boolean = false) => {
    if (reward.status === RewardStatus.ELIGIBLE) {
      return (
        <Button 
          variant="secondary"
          size="sm" 
          className="space-grotesk font-medium text-xs"
          onClick={() => onRedeemClick?.(reward)}
          data-testid={`redeem-button-${index}`}
        >
          {isMobile ? "REDEEM" : "REDEEM NOW"}
        </Button>
      );
    } else if (reward.status === RewardStatus.PENDING) {
      return (
        <div 
          className="inline-flex h-9 items-center justify-center rounded-md px-3 text-xs font-medium border border-[#F4C542] bg-[#FFF6DE] text-[#B45309] space-grotesk font-semibold leading-[140%] tracking-[0.36px] uppercase gap-1 cursor-not-allowed"
          data-testid={`pending-button-${index}`}
        >
          PENDING
        </div>
      );
    } else {
      return (
        <div 
          className="inline-flex h-9 items-center justify-center rounded-md px-3 text-xs font-medium border border-[#EAEAEA] bg-[#E7E7E7] text-[#B0B0B0] space-grotesk font-bold leading-[140%] tracking-[0.36px] uppercase cursor-not-allowed"
          data-testid={`redeemed-button-${index}`}
        >
          REDEEMED
        </div>
      );
    }
  };

  return (
    <div className="lg:grid lg:grid-cols-6 lg:gap-4 p-4 hover:bg-gray-50 transition-colors">
      {/* Desktop Layout */}
      <div className="hidden lg:flex items-center gap-3">
        <Avatar className="h-10 w-10">
          <AvatarImage src={reward.avatar} />
          <AvatarFallback className="bg-gray-200 text-gray-600 text-sm">
            {getInitials(reward.candidateName)}
          </AvatarFallback>
        </Avatar>
        <span className="font-medium text-gray-900 text-sm leading-[140%] tracking-normal">{reward.candidateName}</span>
      </div>

      <div className="hidden lg:flex items-center">
        <span className="text-gray-900 font-medium text-sm leading-[140%] tracking-normal">{reward.jobTitle}</span>
      </div>

      <div className="hidden lg:flex items-center">
        <span className="text-gray-900 font-semibold text-sm leading-[140%] tracking-normal">R{formatRewardAmount(reward.amount)}</span>
      </div>

      <div className="hidden lg:flex items-center">
        <RewardStatusBadge status={reward.status} />
      </div>

      <div className="hidden lg:flex items-center">
        <span className="text-gray-600 font-medium text-sm leading-[140%] tracking-normal">{formatDateForSouthAfrica(reward.date)}</span>
      </div>

      <div className="hidden lg:flex items-center justify-center">
        {renderActionButton(false)}
      </div>

      {/* Mobile Layout */}
      <div className="lg:hidden space-y-3">
        {/* Row 1: Avatar + Name/Job + Amount */}
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-3 flex-1 min-w-0">
            <Avatar className="h-10 w-10 flex-shrink-0">
              <AvatarImage src={reward.avatar} />
              <AvatarFallback className="bg-gray-200 text-gray-600 text-sm">
                {getInitials(reward.candidateName)}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <span className="font-medium text-gray-900 text-sm leading-[140%] tracking-normal block truncate" style={{ fontFamily: 'Manrope' }}>{reward.candidateName}</span>
              <span className="text-gray-600 font-medium text-sm leading-[140%] tracking-normal truncate block" style={{ fontFamily: 'Manrope' }}>{reward.jobTitle}</span>
            </div>
          </div>
          <span className="font-semibold text-gray-900 space-grotesk text-base flex-shrink-0">R{formatRewardAmount(reward.amount)}</span>
        </div>
        
        {/* Row 2: Status + Date */}
        <div className="flex items-center gap-3 flex-wrap">
          <RewardStatusBadge status={reward.status} />
          <span className="text-gray-500 font-medium text-xs leading-[140%] tracking-normal" style={{ fontFamily: 'Manrope' }}>{formatDateForSouthAfrica(reward.date)}</span>
        </div>
        
        {/* Row 3: Action Button - Full Width */}
        {reward.status === RewardStatus.ELIGIBLE && (
          <div className="pt-1">
            <Button 
              variant="secondary"
              size="sm" 
              className="w-full space-grotesk font-medium text-xs h-10"
              onClick={() => onRedeemClick?.(reward)}
              data-testid={`redeem-button-${index}`}
            >
              REDEEM NOW
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
